import mapboxgl from "mapbox-gl";
import { notEmpty } from "../Shared/Utilities/filters";

export type MapDefinition = {
  bounds: mapboxgl.LngLatBounds;
  currentPositionMarker?: mapboxgl.Marker;
  map: mapboxgl.Map;
  markers: mapboxgl.Marker[];
};

export type GeoLocation = {
  latitude: number;
  longitude: number;
};

const defaultBoundsPadding: mapboxgl.PaddingOptions = {
  top: 60,
  right: 30,
  bottom: 30,
  left: 30
};

const maps = new Map<string, MapDefinition>();

/** Haversine great-circle distance in metres between two lng/lat pairs. */
const haversineDistance = (a: mapboxgl.LngLatLike, b: mapboxgl.LngLatLike): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const lngLatA = mapboxgl.LngLat.convert(a);
  const lngLatB = mapboxgl.LngLat.convert(b);
  const R = 6_371_000; // Earth radius in metres
  const dLat = toRad(lngLatB.lat - lngLatA.lat);
  const dLng = toRad(lngLatB.lng - lngLatA.lng);
  const sin2 =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lngLatA.lat)) * Math.cos(toRad(lngLatB.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(sin2));
};

/**
 * Update map bounds to fit either the closest marker or all markers, depending on the `fitTo` parameter.
 * @param mapId The ID of the map to update.
 * @param fitTo Determines whether to fit to the closest marker or all markers.
 */
const updateBounds = (mapId: string, fitTo: "closest" | "all") => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  const { currentPositionMarker, map, markers } = mapDefinition;

  const center = currentPositionMarker?.getLngLat() ?? map.getCenter();

  if (center) {
    let fitToLocations: mapboxgl.LngLatLike[] = [];
    let padding: mapboxgl.PaddingOptions;

    if (fitTo === "closest") {
      const positions = markers.map(m => m.getLngLat()).filter(notEmpty);

      positions.sort(
        (a, b) => haversineDistance(center, a) - haversineDistance(center, b)
      );

      const first = positions[0];
      if (first) {
        fitToLocations = [first];
      }

      padding = { top: 120, right: 50, bottom: 80, left: 50 };
    } else {
      fitToLocations = markers.map(m => m.getLngLat()).filter(notEmpty);
      padding = defaultBoundsPadding;
    }

    // Nothing to fit to (e.g. a search without hits). Leave the map as it is, instead of
    // fitting to the single center point, which zooms all the way in.
    if (fitToLocations.length === 0) {
      return;
    }

    // Only include the user's position, not the current map center. Otherwise the center
    // (which moves to the middle of the previous bounds) shrinks the bounds on every
    // update, making the map zoom in one more level each time.
    const bounds = new mapboxgl.LngLatBounds();

    if (currentPositionMarker) {
      bounds.extend(currentPositionMarker.getLngLat());
    }

    for (const loc of fitToLocations) {
      bounds.extend(loc);
    }

    mapDefinition.bounds = bounds;
    map.fitBounds(bounds, { padding, maxZoom: 14 });
  }
};

export const loadMapbox = (accessToken: string) => {
  mapboxgl.accessToken = accessToken;
};

export const initializeMap = (
  element: HTMLElement,
  mapId: string,
  defaultLat = 0.0,
  defaultLng = 0.0,
  defaultZoom = 5
): mapboxgl.Map => {
  const map = new mapboxgl.Map({
    container: element,
    style: "mapbox://styles/mapbox/streets-v12",
    center: [defaultLng, defaultLat],
    zoom: defaultZoom,
    minZoom: 3,
    maxZoom: 16,
    boxZoom: false,
    projection: "mercator"
  });

  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "bottom-right");

  maps.set(mapId, {
    bounds: new mapboxgl.LngLatBounds(),
    map,
    markers: []
  });

  return map;
};

export const getCurrentPosition = () =>
  new Promise<GeoLocation>((resolve, reject) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        userPosition =>
          resolve({
            latitude: userPosition.coords.latitude,
            longitude: userPosition.coords.longitude
          }),
        error => {
          // PERMISSION_DENIED etc. are constants on the error, so compare them with the code
          if (error.code === error.PERMISSION_DENIED) {
            console.warn("Access to user location has been denied");
          } else if (error.code === error.TIMEOUT) {
            console.error("Timeout when fetching user location");
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            console.error("User location is unavailable");
          } else {
            console.error(
              `Unhandled exception when fetching user location: ${error.code}`,
              error.message
            );
          }

          reject(`[Error code ${error.code}] ${error.message}`);
        }
      );
    } else {
      const error = "Geolocation not supported";
      console.error(error);
      reject(error);
    }
  });

export const updateCurrentPositionPin = (
  mapId: string,
  currentPosition: mapboxgl.LngLatLike
) => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  const { currentPositionMarker, map } = mapDefinition;

  if (currentPositionMarker) {
    currentPositionMarker.setLngLat(currentPosition);
  } else {
    const el = document.getElementById("user-position-marker");

    const marker = new mapboxgl.Marker({ element: el ?? undefined })
      .setLngLat(currentPosition)
      .addTo(map);

    mapDefinition.currentPositionMarker = marker;
  }

  updateBounds(mapId, "closest");
};

export const clearMapMarkers = (mapId: string) => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  for (const marker of mapDefinition.markers) {
    const el = marker.getElement();
    if (el) {
      // Clear id so it is not confused with new content returned by HTMX
      el.id = "";
    }
    marker.remove();
  }

  mapDefinition.markers = [];
};

export type MarkerOptions = {
  element?: HTMLElement | null;
  panOffset?: {
    x?: number;
    y?: number;
  };
  position: { lat: number; lng: number };
  title?: string;
};

const getPanCenter = (
  map: mapboxgl.Map,
  lngLat: mapboxgl.LngLatLike,
  panOffset?: MarkerOptions["panOffset"]
): mapboxgl.LngLatLike => {
  const offsetX = panOffset?.x ?? 0;
  const offsetY = panOffset?.y ?? 0;

  if (offsetX === 0 && offsetY === 0) {
    return lngLat;
  }

  const markerPoint = map.project(lngLat);

  return map.unproject([markerPoint.x - offsetX, markerPoint.y - offsetY]);
};

export const addMapMarkers = (
  mapId: string,
  newMarkers: MarkerOptions[],
  fitToMarkers: boolean = true
) => {
  const mapDefinition = maps.get(mapId);

  if (!mapDefinition) {
    throw `mapId "${mapId}" does not exist`;
  }

  const { map } = mapDefinition;

  for (const opts of newMarkers) {
    const markerOpts: mapboxgl.MarkerOptions = opts.element
      ? { element: opts.element }
      : {};

    const marker = new mapboxgl.Marker(markerOpts)
      .setLngLat([opts.position.lng, opts.position.lat])
      .addTo(map);

    const el = marker.getElement();

    if (opts.element) {
      // Mapbox updates `transform` on the element every render frame to track the
      // map camera. Any CSS transition on `transform` causes the element to animate
      // between frames, making markers visually lag behind the map during panning
      // and fitBounds animations. Override the transition to keep non-position
      // visual effects (hover colour, shadow) while snapping position updates.
      el.style.transitionProperty =
        "background-color, color, border-color, box-shadow, outline-color, opacity, visibility, text-decoration-color";
    }

    el.addEventListener("click", () => {
      const markerLngLat: mapboxgl.LngLatLike = [opts.position.lng, opts.position.lat];

      map.panTo(getPanCenter(map, markerLngLat, opts.panOffset));
    });

    mapDefinition.markers.push(marker);
  }

  if (fitToMarkers) {
    updateBounds(mapId, "all");
  }
};
